import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import BackButton from "@/components/common/BackButton";
import { useToast } from "@/components/ui/use-toast";
import { BillingPageState } from "../billing-settings/components/BillingPageState";
import {
  CustomPageForm,
  type CustomPageFormValues,
} from "./components/CustomPageForm";
import { useCustomPage, useUpdateCustomPage } from "./hooks/useCustomPages";
import { getEmptyRichEditorData } from "@/features/rich-editor";

export const EditCustomPagePage: React.FC = () => {
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const { toast } = useToast();
  const { data: customPage, isLoading, isError } = useCustomPage(id);
  const updateCustomPage = useUpdateCustomPage();
  const defaultValues = useMemo(
    () =>
      customPage
        ? {
            title: customPage.title,
            buttonTitle: customPage.buttonTitle,
            content: customPage.content,
            contentData: customPage.contentData ?? getEmptyRichEditorData(),
            enabled: customPage.enabled,
          }
        : undefined,
    [customPage],
  );

  const handleSubmit = async (data: CustomPageFormValues) => {
    await updateCustomPage.mutateAsync({
      id,
      data,
    });
    toast({
      title: "Success",
      description: "Custom page updated successfully",
    });
    navigate("/custom-pages");
  };

  if (isLoading) {
    return <BillingPageState text="Loading custom page..." />;
  }

  if (isError || !customPage) {
    return <BillingPageState text="Failed to load custom page" tone="error" />;
  }

  return (
    <div className="flex w-full flex-col space-y-8 p-12">
      <BackButton className="self-start" />

      <div className="space-y-2">
        <h1 className="text-3xl font-semibold text-zinc-900">
          Edit Custom Page
        </h1>
        <p className="text-zinc-500">
          Update the guest-facing HTML content and availability.
        </p>
      </div>

      <CustomPageForm
        defaultValues={defaultValues}
        submitLabel="Save changes"
        isSubmitting={updateCustomPage.isPending}
        onSubmit={handleSubmit}
      />
    </div>
  );
};
